import React, { useState } from 'react';
import { ExternalLink, Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { describeFirebaseError } from '../../utils/firebase';
function GoogleGlyph() {
  return <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true" data-id="element-44" data-mp="2d2k1my5d1k5r">
      <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.47a5.54 5.54 0 0 1-2.4 3.63v3h3.86c2.26-2.09 3.56-5.17 3.56-8.87z" data-id="element-45" data-mp="2824qmd8jwoqy" />
      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09A11.99 11.99 0 0 0 12 24z" data-id="element-46" data-mp="1fdqqnrxwjicr" />
      <path fill="#FBBC05" d="M5.27 14.29a7.2 7.2 0 0 1 0-4.58V6.62H1.29a11.99 11.99 0 0 0 0 10.76l3.98-3.09z" data-id="element-47" data-mp="1pbeowrupv7zo" />
      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.7 0 3.99 2.47 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" data-id="element-48" data-mp="35olmde1myual" />
    </svg>;
}
interface GoogleSignInButtonProps {
  onSignedIn?: () => void;
  label?: string;
  className?: string;
}
export function GoogleSignInButton({
  onSignedIn,
  label = 'Continue with Google',
  className = ''
}: GoogleSignInButtonProps) {
  const {
    signInWithGoogle,
    embedded
  } = useAuth();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const openInOwnTab = () => {
    window.open(window.location.href, '_blank', 'noopener,noreferrer');
    toast.info('Opened in a new tab', {
      description: 'Finish signing in there — Google blocks OAuth inside frames.'
    });
  };
  const handleClick = async () => {
    if (embedded) {
      openInOwnTab();
      return;
    }
    setBusy(true);
    try {
      await signInWithGoogle();
      toast.success('Signed in with Google');
      onSignedIn?.();
    } catch (error) {
      setFailed(true);
      const code = typeof error === 'object' && error !== null ? String((error as {
        code?: string;
      }).code ?? '') : '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        toast.warning('Sign-in did not complete', {
          description: describeFirebaseError(error)
        });
      } else {
        toast.error('Google sign-in failed', {
          description: describeFirebaseError(error)
        });
      }
    } finally {
      setBusy(false);
    }
  };
  return <div className={`flex flex-col items-center gap-2 ${className}`} data-id="element-49" data-mp="3k4e3ujyokosq">
      <button type="button" onClick={handleClick} disabled={busy} className="glass-strong group inline-flex h-12 w-full items-center justify-center gap-3 rounded-2xl px-6 text-sm font-semibold text-foreground transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto" data-id="element-50" data-mp="3k4nqpyofifu7">
        {busy ? <Loader2 className="h-5 w-5 animate-spin text-brand" data-id="element-51" data-mp="14dsx0u960uos" /> : embedded ? <ExternalLink className="h-5 w-5 text-brand" data-id="element-52" data-mp="1bx24uj20wlbq" /> : <GoogleGlyph data-id="element-53" data-mp="1byvbvzgjt7k9" />}
        {busy ? 'Opening Google…' : embedded ? 'Open in a new tab to sign in' : label}
      </button>

      {(embedded || failed) && <p className="flex max-w-xs items-start gap-1.5 text-center text-[11px] leading-relaxed text-muted-foreground sm:text-left" data-id="element-54" data-mp="1205v6a0e1t5l">
          <Info className="mt-0.5 h-3 w-3 shrink-0" data-id="element-55" data-mp="16ziqlj5bs8js" />
          {embedded ? 'Google blocks OAuth inside preview frames, so sign-in has to happen in its own browser tab.' : 'If the Google window keeps closing: allow popups, and make sure this domain is listed under Firebase → Authentication → Settings → Authorized domains.'}
        </p>}
    </div>;
}